import { useState, ChangeEvent } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useTranslation } from '../../../contexts/TranslationContext';
import { account_controller } from '../../../controllers/account_controller';
import {
  companyAuthSettingsAtom,
  newAccountModalAtom,
} from '../../../models/atoms/account_atoms';
import { validateNames } from '../../../utils/account_validations';
import { Dialog, DialogActions } from '@mui/material';
import { enqueueSnackbar } from 'notistack';

import { MHMTextField } from '../MHM/MHMTextField';
import { AccountModal } from './AccountModal';
import { MHMTypography } from '../MHM/MHMTypography';
import { ModalPhoneNumberInput } from '../settings/PhoneNumberInput';
import { MHMButton } from '../MHM/MHMButton';
import { MHMDialog } from '../MHM/MHMDialog';
import {
  AccountModalPermissions,
  AccountPermissions,
} from './AccountModalPermissions';
import { useIsMobile } from '../../../hooks/useIsMobile';

export function NewAccountModal() {
  const { t } = useTranslation();
  const [newAccountModal, setNewAccountModal] =
    useRecoilState(newAccountModalAtom);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nameError, setNameError] = useState('');
  const [email, setEmail] = useState('');
  const [emailValidationError, setEmailValidationError] = useState('');
  const [phoneValidationError, setPhoneValidationError] = useState('');
  const [newPhoneNumber, setNewPhoneNumber] = useState('-');
  const [permissions, setPermissions] = useState<AccountPermissions>({
    view: false,
    install: false,
    admin: false,
  });
  const isMobile = useIsMobile();
  const companyAuthSettings = useRecoilValue(companyAuthSettingsAtom);

  const hasErrors = Boolean(
    nameError || emailValidationError || phoneValidationError,
  );
  const isEmpty = Boolean(
    !firstName ||
      !lastName ||
      !email ||
      (!permissions.view && !permissions.install && !permissions.admin),
  );

  function closeModal() {
    setNewAccountModal(false);
  }

  function handleFirstNameChange(e: ChangeEvent<HTMLInputElement>) {
    const newFirstName = e.target.value;
    setFirstName(newFirstName);
    setNameError(validateNames(newFirstName, lastName) ?? '');
  }

  function handleLastNameChange(e: ChangeEvent<HTMLInputElement>) {
    const newLastName = e.target.value;
    setLastName(newLastName);
    setNameError(validateNames(firstName, newLastName) ?? '');
  }

  function validateEmail(e: ChangeEvent<HTMLInputElement>) {
    const newEmail = e.target.value;
    setEmail(newEmail);

    if (
      !newEmail ||
      newEmail.length < 5 ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)
    ) {
      setEmailValidationError('Please provide a valid email address!');
      return;
    }

    setEmailValidationError('');
  }

  const onFormSubmitAccount = async () => {
    if (hasErrors || isEmpty) {
      return;
    }

    try {
      await account_controller.create_account(
        email.toLowerCase(),
        permissions.view,
        permissions.install,
        permissions.admin,
        permissions.admin,
        firstName,
        lastName,
        newPhoneNumber,
      );
      enqueueSnackbar(t('Account created successfully.'), {
        variant: 'success',
        autoHideDuration: 3000,
      });
      setNewAccountModal(false);
    } catch (e) {
      if (!(e instanceof Error)) {
        throw e;
      }

      enqueueSnackbar(t('There was an error creating the account.'), {
        variant: 'error',
        autoHideDuration: 2000,
      });

      // TODO: refactor to better error handling
      switch (e.cause) {
        case 'email':
          setEmailValidationError('This email address is already in use');
          break;
        default:
          throw e;
      }
    }
  };

  return (
    <Dialog
      open={newAccountModal}
      onClose={closeModal}
      sx={{ m: isMobile ? 0 : 4 }}
    >
      <MHMDialog.Header onClose={closeModal}>
        {t('Create New Account')}
      </MHMDialog.Header>

      <MHMDialog.Content dividers>
        <MHMTypography fontSize={20}>{t('Details')}</MHMTypography>

        <AccountModal.FormGroup>
          <MHMTextField
            label={t('First Name')}
            autoComplete="off"
            fullWidth
            required
            value={firstName}
            onChange={handleFirstNameChange}
            error={Boolean(nameError)}
          />
          <MHMTextField
            label={t('Last Name')}
            autoComplete="off"
            fullWidth
            required
            value={lastName}
            onChange={handleLastNameChange}
            error={Boolean(nameError)}
            helperText={t(nameError)}
          />
        </AccountModal.FormGroup>

        <AccountModal.FormGroup>
          <MHMTextField
            label={t('Email')}
            autoComplete="off"
            fullWidth
            required
            value={email}
            onChange={validateEmail}
            error={Boolean(emailValidationError)}
            helperText={t(emailValidationError)}
          />
        </AccountModal.FormGroup>

        <AccountModal.FormGroup>
          <ModalPhoneNumberInput
            label={t(
              'Phone Number (+country code without leading zero or spaces)',
            )}
            setPhoneValidationError={setPhoneValidationError}
            onValid={phone_number => {
              setNewPhoneNumber(phone_number);
            }}
            error={Boolean(phoneValidationError)}
            helperText={t(phoneValidationError)}
          />
        </AccountModal.FormGroup>

        {companyAuthSettings?.auth_type === 'password' && (
          <AccountModal.FormGroup>
            <MHMTypography variant="body2">
              {t(
                'An initial password will be generated and emailed to the new user. They will be asked to change it on first login.',
              )}
            </MHMTypography>
          </AccountModal.FormGroup>
        )}

        <AccountModalPermissions
          label={t('Permissions')}
          required
          permissions={permissions}
          sso_permissions={false}
          onPermissionsChange={setPermissions}
        />
      </MHMDialog.Content>
      <DialogActions>
        <MHMButton
          disabled={hasErrors || isEmpty}
          onClick={onFormSubmitAccount}
        >
          {t('Create New Account')}
        </MHMButton>
      </DialogActions>
    </Dialog>
  );
}
